import DOMPurify from 'dompurify';

/**
 * 輸入清理與驗證工具
 *
 * 用於清理和驗證用戶輸入，防止 XSS 攻擊和資料污染
 */

/**
 * 清理 HTML 字串，防止 XSS 攻擊
 *
 * @param dirty - 未清理的 HTML 字串
 * @returns 清理後的安全 HTML 字串
 *
 * @example
 * sanitizeHTML('<script>alert("XSS")</script><p>Safe content</p>')
 * // Returns: '<p>Safe content</p>'
 */
export function sanitizeHTML(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: []
  });
}

/**
 * 清理純文字輸入
 *
 * @param input - 未清理的文字
 * @param maxLength - 最大長度限制（預設 1000）
 * @returns 清理後的文字
 *
 * @example
 * sanitizeText('  Hello <script>  ', 100)
 * // Returns: 'Hello'
 */
export function sanitizeText(input: string, maxLength: number = 1000): string {
  return input
    .trim()
    .replace(/[<>]/g, '') // 移除 < 和 > 符號
    .substring(0, maxLength); // 限制長度
}

/**
 * 驗證並清理電子郵件
 *
 * @param email - 電子郵件地址
 * @returns 清理後的電子郵件
 * @throws Error 如果電子郵件格式無效
 *
 * @example
 * sanitizeEmail('  User@Example.COM  ')
 * // Returns: 'user@example.com'
 */
export function sanitizeEmail(email: string): string {
  const cleaned = email.trim().toLowerCase();

  // RFC 5322 簡化版正則表達式
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (!emailRegex.test(cleaned)) {
    throw new Error('無效的電子郵件格式');
  }

  // 防止過長的電子郵件（RFC 建議最大 254 字元）
  if (cleaned.length > 254) {
    throw new Error('電子郵件地址過長');
  }

  return cleaned;
}

/**
 * 清理電話號碼
 *
 * @param phone - 電話號碼
 * @returns 清理後的電話號碼
 *
 * @example
 * sanitizePhone('(02) 1234-5678 ext.99')
 * // Returns: '02-1234-5678'
 */
export function sanitizePhone(phone: string): string {
  // 只保留數字、+ 和 -
  const cleaned = phone.replace(/[^\d+\-]/g, '');

  // 限制長度（國際電話號碼最長約 15 位）
  return cleaned.substring(0, 20);
}

/**
 * 清理並驗證姓名
 *
 * @param name - 姓名
 * @returns 清理後的姓名
 * @throws Error 如果姓名格式無效
 *
 * @example
 * sanitizeName('  張三  ')
 * // Returns: '張三'
 */
export function sanitizeName(name: string): string {
  const cleaned = name.trim();

  if (cleaned.length < 2) {
    throw new Error('姓名至少需要 2 個字元');
  }

  if (cleaned.length > 50) {
    throw new Error('姓名不能超過 50 個字元');
  }

  // 只允許中文、英文、空格和常見符號（如·）
  if (!/^[\u4e00-\u9fa5a-zA-Z\s·]+$/.test(cleaned)) {
    throw new Error('姓名只能包含中文、英文或空格');
  }

  return cleaned;
}

/**
 * 清理教會名稱
 *
 * @param church - 教會名稱
 * @returns 清理後的教會名稱
 * @throws Error 如果教會名稱無效
 */
export function sanitizeChurch(church: string): string {
  const cleaned = church.trim();

  if (cleaned.length < 2) {
    throw new Error('教會名稱至少需要 2 個字元');
  }

  if (cleaned.length > 100) {
    throw new Error('教會名稱不能超過 100 個字元');
  }

  // 允許中文、英文、數字、空格和常見符號
  if (!/^[\u4e00-\u9fa5a-zA-Z0-9\s()（）·]+$/.test(cleaned)) {
    throw new Error('教會名稱包含無效字元');
  }

  return cleaned;
}

/**
 * 清理備註訊息
 *
 * @param message - 備註訊息
 * @returns 清理後的訊息
 */
export function sanitizeMessage(message: string): string {
  const cleaned = message.trim();

  // 限制長度為 500 字元
  if (cleaned.length > 500) {
    throw new Error('備註不能超過 500 個字元');
  }

  // 移除潛在危險的字元，但保留基本標點符號
  return cleaned.replace(/[<>]/g, '');
}

/**
 * 清理職位描述
 *
 * @param description - 職位描述
 * @returns 清理後的描述
 */
export function sanitizeRoleDescription(description: string): string {
  const cleaned = description.trim();

  if (cleaned.length > 100) {
    throw new Error('職位描述不能超過 100 個字元');
  }

  return cleaned.replace(/[<>]/g, '');
}

/**
 * 驗證性別
 *
 * @param gender - 性別（中文）
 * @returns 驗證後的性別
 * @throws Error 如果性別無效
 */
export function validateGender(gender: string): '男' | '女' {
  if (gender !== '男' && gender !== '女') {
    throw new Error('性別必須是「男」或「女」');
  }
  return gender;
}

/**
 * 驗證地區
 *
 * @param district - 地區代碼
 * @param validDistricts - 有效地區列表
 * @returns 驗證後的地區
 * @throws Error 如果地區無效
 */
export function validateDistrict(district: string, validDistricts: string[]): string {
  if (!validDistricts.includes(district)) {
    throw new Error('無效的地區選擇');
  }
  return district;
}

/**
 * 驗證職位
 *
 * @param role - 職位代碼
 * @param validRoles - 有效職位列表
 * @returns 驗證後的職位
 * @throws Error 如果職位無效
 */
export function validateRole(role: string, validRoles: string[]): string {
  if (!validRoles.includes(role)) {
    throw new Error('無效的職位選擇');
  }
  return role;
}

/**
 * 驗證活動選擇
 *
 * @param selectedEvents - 已選擇的活動 ID 列表
 * @param validEventIds - 有效的活動 ID 列表
 * @returns 驗證後的活動列表
 * @throws Error 如果有無效的活動 ID
 */
export function validateEvents(selectedEvents: string[], validEventIds: string[]): string[] {
  if (!Array.isArray(selectedEvents)) {
    throw new Error('活動選擇必須是陣列');
  }

  if (selectedEvents.length === 0) {
    throw new Error('請至少選擇一個梯隊');
  }

  for (const eventId of selectedEvents) {
    if (!validEventIds.includes(eventId)) {
      throw new Error(`無效的梯隊選擇: ${eventId}`);
    }
  }

  return Array.from(new Set(selectedEvents));
}

/**
 * 通用輸入清理
 *
 * @param input - 任意輸入
 * @returns 清理後的字串
 */
export function sanitizeInput(input: unknown): string {
  if (typeof input !== 'string') {
    return '';
  }

  return sanitizeText(input);
}

/**
 * 清理物件中的所有字串欄位
 *
 * @param obj - 要清理的物件
 * @returns 清理後的物件
 */
export function sanitizeObject<T extends Record<string, any>>(obj: T): T {
  const cleaned: any = {};

  for (const key in obj) {
    const value = obj[key];

    if (typeof value === 'string') {
      cleaned[key] = sanitizeText(value);
    } else if (Array.isArray(value)) {
      cleaned[key] = value.map((item: any) =>
        typeof item === 'string' ? sanitizeText(item) : item
      );
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned as T;
}

/**
 * 安全地解析 JSON
 *
 * @param jsonString - JSON 字串
 * @returns 解析後的物件或 null
 */
export function safeJSONParse<T = any>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('JSON 解析失敗:', error);
    return null;
  }
}

/**
 * 防止 SQL 注入的字串清理（雖然 Firestore 不使用 SQL，但作為額外保護）
 *
 * @param input - 輸入字串
 * @returns 清理後的字串
 */
export function preventSQLInjection(input: string): string {
  // 移除常見的 SQL 關鍵字和特殊字元
  const dangerous = /('|"|;|--|\*|\/\*|\*\/|xp_|sp_|exec|execute|select|insert|update|delete|drop|create|alter)/gi;
  return input.replace(dangerous, '');
}

/**
 * 驗證 URL 是否安全
 *
 * @param url - URL 字串
 * @returns 是否為安全的 URL
 */
export function isSafeURL(url: string): boolean {
  try {
    const parsed = new URL(url);
    // 只允許 http 和 https 協議
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
